<script lang="ts">
  import { authState, getIdentity, logout } from '$lib/auth.svelte'
  import { claimServerModal } from '$lib/claim-server.svelte'
  import { openGrantAccess } from '$lib/grant-access.svelte'
  import { refreshServerAccess } from '$lib/server-access.svelte'
  import * as Avatar from '$lib/components/ui/avatar/index'
  import { Badge } from '$lib/components/ui/badge/index.js'
  import { Button } from '$lib/components/ui/button/index'
  import { goto, route } from '@mateothegreat/svelte5-router'
  import LogOutIcon from '@lucide/svelte/icons/log-out'
  import MailIcon from '@lucide/svelte/icons/mail'
  import ServerIcon from '@lucide/svelte/icons/server'
  import SettingsIcon from '@lucide/svelte/icons/settings'
  import UserIcon from '@lucide/svelte/icons/user'
  import UserPlusIcon from '@lucide/svelte/icons/user-plus'
  import ShieldIcon from '@lucide/svelte/icons/shield'
  import { serverHash } from '$lib/utils'
  import { Switch } from '$lib/components/ui/switch/index.js'
  import { Label } from '$lib/components/ui/label/index.js'
  import {
    getUserSettings,
    updateUserSettings,
    type UserSettings,
  } from '$lib/apis/user/api'
  import {
    loadPreferences,
    prefs,
    resetPreferences,
    setDisplayNsfw,
  } from '$lib/preferences.svelte'
  import EyeIcon from '@lucide/svelte/icons/eye'

  let loadingServers = $state(false)
  let loadingSettings = $state(false)
  let savingSettings = $state(false)
  let settingsError = $state<string | null>(null)
  let settings = $state<UserSettings>({
    hideFromDirectory: false,
    blockInvitations: false,
  })

  $effect(() => {
    void getIdentity()
    loadPreferences()
    // Gateway list lands first; deployment roles patch in progressively.
    loadingServers = true
    void refreshServerAccess()
      .catch((err) => {
        console.error('load servers failed', err)
      })
      .finally(() => {
        loadingServers = false
      })
    loadingSettings = true
    void getUserSettings()
      .then((s) => {
        settings = {
          hideFromDirectory: !!s.hideFromDirectory,
          blockInvitations: !!s.blockInvitations,
        }
        settingsError = null
      })
      .catch((err) => {
        console.error('load user settings failed', err)
        settingsError = 'Could not load privacy settings.'
      })
      .finally(() => {
        loadingSettings = false
      })
  })

  async function persistSettings(next: UserSettings) {
    const prev = { ...settings }
    settings = next
    savingSettings = true
    settingsError = null
    try {
      const saved = await updateUserSettings(next)
      settings = {
        hideFromDirectory: !!saved.hideFromDirectory,
        blockInvitations: !!saved.blockInvitations,
      }
    } catch (err) {
      console.error('update user settings failed', err)
      settings = prev
      settingsError = 'Could not save privacy settings.'
    } finally {
      savingSettings = false
    }
  }

  async function onHideFromDirectoryChange(checked: boolean) {
    if (loadingSettings || savingSettings) return
    await persistSettings({ ...settings, hideFromDirectory: checked })
  }

  async function onBlockInvitationsChange(checked: boolean) {
    if (loadingSettings || savingSettings) return
    await persistSettings({ ...settings, blockInvitations: checked })
  }

  function onDisplayNsfwChange(checked: boolean) {
    setDisplayNsfw(checked)
  }

  const displayName = $derived(
    authState.name && authState.name !== 'Not Connected'
      ? authState.name
      : 'Reader'
  )
  const displayEmail = $derived(authState.email || '')
  const avatarInitials = $derived(
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || '?'
  )
  const servers = $derived(authState.servers ?? [])

  function roleAccent(role: string) {
    const r = role.toUpperCase()
    if (r === 'OWNER') {
      return 'border-latte-green/40 bg-latte-green/10 text-latte-green dark:border-macchiato-green/40 dark:bg-macchiato-green/10 dark:text-macchiato-green'
    }
    if (r === 'ADMIN') {
      return 'border-latte-peach/40 bg-latte-peach/10 text-latte-peach dark:border-macchiato-peach/40 dark:bg-macchiato-peach/10 dark:text-macchiato-peach'
    }
    if (r === 'MEMBER') {
      return 'border-latte-sky/40 bg-latte-sky/10 text-latte-sky dark:border-macchiato-sky/40 dark:bg-macchiato-sky/10 dark:text-macchiato-sky'
    }
    return 'border-latte-surface1/60 bg-latte-base/40 text-latte-text/70 dark:border-macchiato-surface1/60 dark:bg-macchiato-base/40 dark:text-macchiato-text/70'
  }

  function displayRoles(server: (typeof servers)[number]) {
    if (server.roles?.length) return server.roles
    // Fallback while a deployment is unreachable: gateway membership only.
    return server.membership ? [server.membership] : []
  }

  function canManageServer(server: (typeof servers)[number]) {
    const roles = displayRoles(server).map((r) => String(r).toUpperCase())
    return roles.includes('OWNER') || roles.includes('ADMIN')
  }

  async function handleLogout() {
    try {
      await logout()
    } finally {
      resetPreferences()
      goto('/login')
    }
  }
</script>

<div class="mx-4 mt-20 mb-10 max-w-xl space-y-4 sm:mx-10 md:mx-auto">
  <div
    class="flex min-w-0 flex-col gap-0.5 rounded-md border border-latte-surface1/60
      bg-latte-mantle/85 px-3 py-1.5 shadow-sm backdrop-blur-sm
      dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/85"
  >
    <span class="text-[8px] leading-none font-light text-latte-green dark:text-macchiato-green">
      account
    </span>
    <h1
      class="truncate text-lg font-semibold leading-tight text-latte-text
        dark:text-macchiato-text sm:text-xl"
    >
      Profile
    </h1>
  </div>

  <section
    class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
      shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
  >
    <div
      class="flex flex-col items-center gap-4 border-b border-latte-surface1/50 px-6 py-8
        sm:flex-row sm:items-start sm:gap-5
        dark:border-macchiato-surface1/50"
    >
      <Avatar.Root
        class="h-20 w-20 shrink-0 ring-2 ring-latte-green/50
          dark:ring-macchiato-green/50"
      >
        <Avatar.Fallback
          class="bg-latte-base text-2xl font-semibold text-latte-green
            dark:bg-macchiato-base dark:text-macchiato-green"
        >
          {avatarInitials}
        </Avatar.Fallback>
      </Avatar.Root>
      <div class="min-w-0 flex flex-col items-center gap-1 text-center sm:items-start sm:text-left">
        <p class="truncate text-xl font-semibold text-latte-text dark:text-macchiato-text">
          {displayName}
        </p>
        {#if displayEmail}
          <p class="truncate text-sm text-latte-text/60 dark:text-macchiato-text/60">
            {displayEmail}
          </p>
        {/if}
      </div>
    </div>

    <dl class="divide-y divide-latte-surface1/50 dark:divide-macchiato-surface1/50">
      <div class="flex items-start gap-3 px-5 py-4">
        <UserIcon
          class="mt-0.5 size-4 shrink-0 text-latte-green dark:text-macchiato-green"
          strokeWidth={1.75}
        />
        <div class="min-w-0 flex flex-col gap-0.5">
          <dt
            class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
              dark:text-macchiato-text/55"
          >
            Username
          </dt>
          <dd class="truncate text-sm font-medium text-latte-text dark:text-macchiato-text">
            {displayName}
          </dd>
        </div>
      </div>
      <div class="flex items-start gap-3 px-5 py-4">
        <MailIcon
          class="mt-0.5 size-4 shrink-0 text-latte-sky dark:text-macchiato-sky"
          strokeWidth={1.75}
        />
        <div class="min-w-0 flex flex-col gap-0.5">
          <dt
            class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
              dark:text-macchiato-text/55"
          >
            Email
          </dt>
          <dd class="truncate text-sm font-medium text-latte-text dark:text-macchiato-text">
            {displayEmail || '—'}
          </dd>
        </div>
      </div>
    </dl>

    <div class="border-t border-latte-surface1/50 px-5 py-4 dark:border-macchiato-surface1/50">
      <Button
        variant="outline"
        class="w-full gap-2 border-latte-red/40 text-latte-red hover:bg-latte-red/10
          dark:border-macchiato-red/40 dark:text-macchiato-red dark:hover:bg-macchiato-red/10"
        onclick={handleLogout}
      >
        <LogOutIcon class="size-4" strokeWidth={1.75} />
        Sign out
      </Button>
    </div>
  </section>

  <section
    class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
      shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
  >
    <div
      class="flex items-center gap-2 border-b border-latte-surface1/50 px-5 py-3.5
        dark:border-macchiato-surface1/50"
    >
      <ShieldIcon
        class="size-4 shrink-0 text-latte-mauve dark:text-macchiato-mauve"
        strokeWidth={1.75}
      />
      <div class="min-w-0">
        <p
          class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
            dark:text-macchiato-text/55"
        >
          Privacy
        </p>
        <p class="text-sm font-semibold text-latte-text dark:text-macchiato-text">
          {#if loadingSettings}
            Loading…
          {:else}
            Federation preferences
          {/if}
        </p>
      </div>
    </div>

  {#if settingsError}
    <p class="px-5 pt-4 text-sm text-latte-red dark:text-macchiato-red">{settingsError}</p>
  {/if}

    <div class="divide-y divide-latte-surface1/50 dark:divide-macchiato-surface1/50">
      <div class="flex items-center justify-between gap-4 px-5 py-4">
        <div class="min-w-0 flex flex-col gap-0.5">
          <Label
            for="hide-from-directory"
            class="text-sm font-medium text-latte-text dark:text-macchiato-text"
          >
            Hide from directory
          </Label>
          <p class="text-xs leading-snug text-latte-text/55 dark:text-macchiato-text/55">
            Your username won't appear when others search the user directory.
          </p>
        </div>
        <Switch
          id="hide-from-directory"
          checked={settings.hideFromDirectory}
          disabled={loadingSettings || savingSettings}
          onCheckedChange={onHideFromDirectoryChange}
        />
      </div>
      <div class="flex items-center justify-between gap-4 px-5 py-4">
        <div class="min-w-0 flex flex-col gap-0.5">
          <Label
            for="block-invitations"
            class="text-sm font-medium text-latte-text dark:text-macchiato-text"
          >
            Block invitations
          </Label>
          <p class="text-xs leading-snug text-latte-text/55 dark:text-macchiato-text/55">
            Server owners can't send you new invites through the gateway.
          </p>
        </div>
        <Switch
          id="block-invitations"
          checked={settings.blockInvitations}
          disabled={loadingSettings || savingSettings}
          onCheckedChange={onBlockInvitationsChange}
        />
      </div>
    </div>
  </section>

  <section
    class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
      shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
  >
    <div
      class="flex items-center gap-2 border-b border-latte-surface1/50 px-5 py-3.5
        dark:border-macchiato-surface1/50"
    >
      <EyeIcon
        class="size-4 shrink-0 text-latte-sky dark:text-macchiato-sky"
        strokeWidth={1.75}
      />
      <div class="min-w-0">
        <p
          class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
            dark:text-macchiato-text/55"
        >
          Preferences
        </p>
        <p class="text-sm font-semibold text-latte-text dark:text-macchiato-text">
          Library display
        </p>
      </div>
    </div>

    <div class="divide-y divide-latte-surface1/50 dark:divide-macchiato-surface1/50">
      <div class="flex items-center justify-between gap-4 px-5 py-4">
        <div class="min-w-0 flex flex-col gap-0.5">
          <Label
            for="display-nsfw"
            class="text-sm font-medium text-latte-text dark:text-macchiato-text"
          >
            Display NSFW series
          </Label>
          <p class="text-xs leading-snug text-latte-text/55 dark:text-macchiato-text/55">
            Show series marked as adult.
          </p>
        </div>
        <Switch
          id="display-nsfw"
          checked={prefs.displayNsfw}
          onCheckedChange={onDisplayNsfwChange}
        />
      </div>
    </div>
  </section>

  <section
    class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
      shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
  >
    <div
      class="flex items-center justify-between gap-3 border-b border-latte-surface1/50 px-5 py-3.5
        dark:border-macchiato-surface1/50"
    >
      <div class="flex min-w-0 items-center gap-2">
        <ServerIcon
          class="size-4 shrink-0 text-latte-peach dark:text-macchiato-peach"
          strokeWidth={1.75}
        />
        <div class="min-w-0">
          <p
            class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
              dark:text-macchiato-text/55"
          >
            Servers
          </p>
          <p class="text-sm font-semibold text-latte-text dark:text-macchiato-text">
            {#if loadingServers && servers.length === 0}
              Loading…
            {:else if servers.length === 0}
              No servers yet
            {:else}
              {servers.length} server{servers.length === 1 ? '' : 's'}
            {/if}
          </p>
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        class="shrink-0"
        onclick={() => (claimServerModal.open = true)}
      >
        Claim
      </Button>
    </div>

    {#if loadingServers && servers.length === 0}
      <p class="px-5 py-6 text-sm text-latte-text/60 dark:text-macchiato-text/60">
        Loading servers…
      </p>
    {:else if servers.length === 0}
      <p class="px-5 py-6 text-sm text-latte-text/60 dark:text-macchiato-text/60">
        Claim a server or accept an invite to see it here with your roles.
      </p>
    {:else}
      <ul class="divide-y divide-latte-surface1/50 dark:divide-macchiato-surface1/50">
        {#each servers as server (server.uid)}
          {@const roles = displayRoles(server)}
          {@const isOwner = server.membership === 'OWNER'}
          {@const manage = canManageServer(server)}
          <li class="flex items-stretch gap-0">
            <a
              href={manage ? `/media/${server.uid}/settings` : `/media/${server.uid}`}
              use:route
              class="flex min-w-0 flex-1 items-center justify-between gap-3 px-5 py-3.5
                transition-colors hover:bg-latte-surface1/30
                dark:hover:bg-macchiato-surface1/30"
            >
              <div class="min-w-0 flex flex-col gap-0.5">
                <span
                  class="truncate text-sm font-medium text-latte-text dark:text-macchiato-text"
                >
                  {server.name || 'Untitled server'}
                  <span class="font-normal text-latte-text/50 dark:text-macchiato-text/50">
                    #{serverHash(server.uid)}
                  </span>
                </span>
                <span
                  class="truncate text-[10px] text-latte-text/45 dark:text-macchiato-text/45"
                >
                  {#if server.ownerName}
                    owner {server.ownerName}{#if server.ownerHash}<span class="opacity-70">
                        #{server.ownerHash}</span
                      >{/if}
                  {:else if server.membership === 'OWNER'}
                    claimed on gateway
                  {:else}
                    member on gateway
                  {/if}
                </span>
              </div>
              <div class="flex shrink-0 flex-wrap justify-end gap-1">
                {#each roles as role (role)}
                  <Badge
                    variant="outline"
                    class="rounded-md font-medium capitalize {roleAccent(role)}"
                  >
                    {role.toLowerCase()}
                  </Badge>
                {/each}
              </div>
            </a>
            <div class="flex shrink-0 items-center gap-0.5 pr-3">
              {#if isOwner}
                <Button
                  size="sm"
                  variant="ghost"
                  class="gap-1.5 text-latte-text/70 hover:text-latte-green
                    dark:text-macchiato-text/70 dark:hover:text-macchiato-green"
                  title="Grant Access"
                  onclick={() => openGrantAccess(server.uid, server.name ?? '')}
                >
                  <UserPlusIcon class="size-4" strokeWidth={1.75} />
                  <span class="hidden sm:inline">Invite</span>
                </Button>
              {/if}
            </div>
          </li>
        {/each}
      </ul>
    {/if}
  </section>
</div>
